import { useMemo } from "react";
import { keepPreviousData } from "@tanstack/react-query";
import { trpc } from "../trpc";
import { AssetTypes, chunkArray } from "@zapier/utils";
import { FolderLookupMap } from "@zapier/bff";

const FOLDER_LOOKUP_CHUNK_SIZE = 25;

interface Input {
  assetType: AssetTypes;
  assetIds: string[];
  enabled?: boolean;
}

export function useFoldersLookup({
  assetType,
  assetIds,
  enabled = true,
}: Input) {
  const chunks = useMemo(
    () => chunkArray(assetIds, FOLDER_LOOKUP_CHUNK_SIZE),
    [assetIds],
  );

  const formattedChunks = useMemo(
    () =>
      chunks.map((chunk) => chunk.map((id) => `${assetType}:${id}`).join(",")),
    [assetType, chunks],
  );

  const queries = trpc.useQueries((t) =>
    formattedChunks.map((formattedIds, index) =>
      t.folder.foldersLookup(
        {
          assetIds: formattedIds,
          limit: chunks[index]?.length ?? FOLDER_LOOKUP_CHUNK_SIZE,
          offset: 0,
        },
        {
          enabled: enabled && assetIds.length > 0 && formattedIds.length > 0,
          placeholderData: keepPreviousData,
        },
      ),
    ),
  );

  const isLoading = queries.some((q) => q.isLoading);
  const isFetching = queries.some((q) => q.isFetching);
  const isError = queries.some((q) => q.isError);
  const error = queries.find((q) => q.error)?.error;

  const lookupKey = queries.map((q) => q.dataUpdatedAt).join(",");

  const folderLookup: FolderLookupMap = useMemo(() => {
    const lookup: FolderLookupMap = {};
    for (const query of queries) {
      if (query.data?.results) {
        for (const result of query.data.results) {
          lookup[result.assetId] = result.folder || undefined;
        }
      }
    }
    return lookup;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- queries is a new array ref each render; use lookupKey (derived from dataUpdatedAt) to only recompute when query data actually changes
  }, [lookupKey]);

  return {
    folderLookup,
    isLoading,
    isFetching,
    isError,
    error,
  };
}
